const NOT_DETERMINED = 'NOT_DETERMINED'

function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function finiteNonNegative(value) {
    if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) return null
    const number = Number(value)
    return Number.isFinite(number) ? Math.max(0, number) : null
}

function decisionValue(decision) {
    if (!isObject(decision)) return null

    const fields = ['allocatedCapital', 'investmentAmount', 'capitalRequired', 'totalRiskCost']
    for (const field of fields) {
        const value = finiteNonNegative(decision[field])
        if (value !== null) return value
    }
    return null
}

function evidenceId(evidence) {
    if (typeof evidence === 'string' && evidence.trim() !== '') return evidence
    if (typeof evidence === 'number' && Number.isFinite(evidence)) return evidence
    if (!isObject(evidence)) return null

    for (const field of ['id', 'evidenceId', 'key', 'type']) {
        const value = evidence[field]
        if ((typeof value === 'string' && value.trim() !== '') || (typeof value === 'number' && Number.isFinite(value))) {
            return value
        }
    }
    return null
}

function identity(value) {
    return `${typeof value}:${value}`
}

function normalizeEvidence(items) {
    const result = []
    const seen = new Set()
    ;(Array.isArray(items) ? items : []).forEach((item) => {
        const id = evidenceId(item)
        if (id === null || seen.has(identity(id))) return
        seen.add(identity(id))
        result.push(id)
    })
    return result
}

function normalizeThresholds(value) {
    const thresholds = Array.isArray(value) ? value : isObject(value) && Array.isArray(value.authorityThresholds) ? value.authorityThresholds : []

    return thresholds
        .map((threshold, index) => {
            if (!isObject(threshold)) return null
            const authority = threshold.authority ?? threshold.requiredAuthority
            const maximum = finiteNonNegative(threshold.maxEconomicValue ?? threshold.maxAmount ?? threshold.threshold)
            if (typeof authority !== 'string' || authority.trim() === '' || maximum === null) return null
            return {
                authority,
                maximum,
                escalationAuthority:
                    typeof threshold.escalationAuthority === 'string' && threshold.escalationAuthority.trim() !== ''
                        ? threshold.escalationAuthority
                        : null,
                index
            }
        })
        .filter(Boolean)
        .sort((left, right) => left.maximum - right.maximum || left.index - right.index)
}

function authorityFor(economicValue, thresholds) {
    if (economicValue === null || thresholds.length === 0) return null
    return thresholds.find(({ maximum }) => economicValue <= maximum) || thresholds[thresholds.length - 1]
}

function policyEscalations(decision) {
    if (!isObject(decision)) return []
    const source = Array.isArray(decision.escalationRequirements)
        ? decision.escalationRequirements
        : decision.policyEligibility === 'ESCALATION_REQUIRED' || decision.escalationRequired === true
        ? ['POLICY_ESCALATION_REQUIRED']
        : []

    return source.filter((item) => typeof item === 'string' && item.trim() !== '')
}

/**
 * Determines whether a selected economic decision is ready to enter governance.
 * This is a deterministic routing/readiness assessment; it does not record an
 * approval or otherwise exercise governance authority.
 */
function evaluateGovernanceDecision(input = {}) {
    const source = isObject(input) ? input : {}
    const selectedEconomicDecision = source.selectedEconomicDecision ?? null
    const governanceThresholds = source.governanceThresholds ?? []
    const requiredEvidence = source.requiredEvidence ?? []
    const availableEvidence = source.availableEvidence ?? []
    const decisionPresent = isObject(selectedEconomicDecision)
    const economicValue = decisionValue(selectedEconomicDecision)
    const thresholds = normalizeThresholds(governanceThresholds)
    const authorityRule = authorityFor(economicValue, thresholds)
    const required = normalizeEvidence(requiredEvidence)
    const available = normalizeEvidence(availableEvidence)
    const availableIds = new Set(available.map(identity))
    const satisfiedEvidence = required.filter((id) => availableIds.has(identity(id)))
    const missingEvidence = required.filter((id) => !availableIds.has(identity(id)))
    const completenessRatio = required.length === 0 ? 1 : satisfiedEvidence.length / required.length
    const escalationRequirements = policyEscalations(selectedEconomicDecision)

    if (economicValue !== null && authorityRule && economicValue > authorityRule.maximum) {
        escalationRequirements.push(
            authorityRule.escalationAuthority
                ? `ESCALATE_TO_${authorityRule.escalationAuthority}`
                : 'AUTHORITY_THRESHOLD_EXCEEDED'
        )
    }

    const uniqueEscalations = [...new Set(escalationRequirements)]
    const pendingGovernanceActions = []
    if (!decisionPresent) pendingGovernanceActions.push('SELECT_ECONOMIC_DECISION')
    if (decisionPresent && economicValue === null) pendingGovernanceActions.push('PROVIDE_ECONOMIC_VALUE')
    if (authorityRule === null) pendingGovernanceActions.push('CONFIGURE_GOVERNANCE_AUTHORITY')
    missingEvidence.forEach((id) => pendingGovernanceActions.push(`PROVIDE_EVIDENCE:${id}`))
    uniqueEscalations.forEach((requirement) => pendingGovernanceActions.push(`RESOLVE_ESCALATION:${requirement}`))

    const isReady =
        decisionPresent && economicValue !== null && authorityRule !== null && missingEvidence.length === 0 && uniqueEscalations.length === 0

    return {
        requiredAuthority: authorityRule?.authority ?? NOT_DETERMINED,
        escalationRequirements: uniqueEscalations,
        evidenceCompleteness: {
            status: missingEvidence.length === 0 ? 'COMPLETE' : 'INCOMPLETE',
            requiredEvidence: required,
            satisfiedEvidence,
            missingEvidence,
            completenessRatio
        },
        governanceReadiness: isReady ? 'READY' : 'NOT_READY',
        pendingGovernanceActions
    }
}

export function createGovernanceDecision(policyResult = {}, governancePolicy = {}, evidence = {}) {
    const policy = isObject(policyResult) ? policyResult : {}
    const evidenceSource = isObject(evidence) ? evidence : {}

    return evaluateGovernanceDecision({
        selectedEconomicDecision: policy.selectedAlternative ?? null,
        governanceThresholds: governancePolicy,
        requiredEvidence: evidenceSource.requiredEvidence ?? evidenceSource.required ?? [],
        availableEvidence: evidenceSource.availableEvidence ?? evidenceSource.available ?? []
    })
}
